import { api } from "./api";

export interface MentorProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  user: string;
  name: string;
  title: string;
  company: string;
  bio: string;
  expertise: string[];
  pricePerSession: number;
  rating: number;
  totalSessions: number;
  totalStudents: number;
  isVerified: boolean;
  isTopMentor: boolean;
  avatar?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  facebook?: string;
  hourlyRate?: number;
  yearsOfExperience?: number;
  languages?: string[];
  availability?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }[];
}

export interface MentorshipSession {
  _id: string;
  mentor: string;
  student: string;
  scheduledAt: string;
  duration: number;
  status: "pending" | "approved" | "confirmed" | "completed" | "cancelled";
  price: number;
  topic: string;
  notes?: string;
  meetingLink?: string;
  zoomMeetingId?: string;
  googleCalendarEventId?: string;
  createdAt: string;
}

export interface MentorshipMessage {
  _id: string;
  sender: string;
  recipient: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

export interface MentorshipAvailability {
  _id: string;
  mentor: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isBooked: boolean;
}

export interface MentorshipReview {
  _id: string;
  mentor: string;
  student: string;
  session: string;
  rating: number;
  comment: string;
  createdAt: string;
  studentName?: string;
  studentAvatar?: string;
}

export const mentorshipApi = {
  // Get all mentors with filters
  getMentors: (options?: {
    page?: number;
    limit?: number;
    search?: string;
    expertise?: string;
    company?: string;
    minPrice?: number;
    maxPrice?: number;
    minRating?: number;
    verified?: boolean;
    topMentor?: boolean;
    sortBy?: "price" | "rating" | "sessions" | "students" | "createdAt";
    sortOrder?: "asc" | "desc";
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.search) params.append("search", options.search);
    if (options?.expertise) params.append("expertise", options.expertise);
    if (options?.company) params.append("company", options.company);
    if (options?.minPrice) params.append("minPrice", String(options.minPrice));
    if (options?.maxPrice) params.append("maxPrice", String(options.maxPrice));
    if (options?.minRating) params.append("minRating", String(options.minRating));
    if (options?.verified !== undefined) params.append("verified", String(options.verified));
    if (options?.topMentor !== undefined) params.append("topMentor", String(options.topMentor));
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);

    return api.get<{ success: boolean; data: MentorProfile[]; pagination: any }>(
      `/mentorship/mentors${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Get mentor by ID
  getMentorById: (mentorId: string) => {
    return api.get<{ success: boolean; data: MentorProfile }>(`/mentorship/mentors/${mentorId}`);
  },

  // Get current user's mentor profile
  getMyMentorProfile: () => {
    return api.get<{ success: boolean; data: MentorProfile }>("/mentorship/mentors/me");
  },

  // Get mentor profile by user ID (for mentor to access their public profile)
  getMentorByUserId: (userId: string) => {
    return api.get<{ success: boolean; data: MentorProfile }>(`/mentorship/mentors/by-user/${userId}`);
  },

  // Create mentor profile
  createMentorProfile: (data: Partial<MentorProfile>) => {
    return api.post<{ success: boolean; message: string; data: MentorProfile }>(
      "/mentorship/mentors/me",
      data
    );
  },

  // Update mentor profile
  updateMentorProfile: (data: Partial<MentorProfile>) => {
    return api.put<{ success: boolean; message: string; data: MentorProfile }>(
      "/mentorship/mentors/me",
      data
    );
  },

  // Delete mentor profile
  deleteMentorProfile: () => {
    return api.delete<{ success: boolean; message: string }>("/mentorship/mentors/me");
  },

  // Book a session
  bookSession: (mentorId: string, scheduledAt: string, topic: string, notes?: string) => {
    return api.post<{ success: boolean; message: string; data: MentorshipSession }>(
      "/mentorship/sessions/book",
      { mentorId, scheduledAt, topic, notes }
    );
  },

  // Confirm a session (for paid sessions)
  confirmSession: (sessionId: string) => {
    return api.put<{ success: boolean; message: string; data: MentorshipSession }>(
      `/mentorship/sessions/${sessionId}/confirm`
    );
  },

  // Cancel a session
  cancelSession: (sessionId: string) => {
    return api.put<{ success: boolean; message: string }>(`/mentorship/sessions/${sessionId}/cancel`);
  },

  // Complete a session
  completeSession: (sessionId: string) => {
    return api.put<{ success: boolean; message: string; data: MentorshipSession }>(`/mentorship/sessions/${sessionId}/complete`);
  },

  // Get my sessions (as student or mentor)
  getMySessions: (options?: { role?: "student" | "mentor"; status?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.role) params.append("role", options.role);
    if (options?.status) params.append("status", options.status);
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    return api.get<{ success: boolean; data: MentorshipSession[]; pagination: any }>(
      `/mentorship/sessions/my${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Get mentor stats
  getMentorStats: () => {
    return api.get<{
      success: boolean;
      data: {
        totalMentees: number;
        totalSessionsThisMonth: number;
        totalEarnings: number;
        averageRating: number;
        totalReviews: number;
        totalSessions: number;
        completedSessions: number;
        pendingSessions: number;
        cancelledSessions: number;
      };
    }>("/mentorship/mentors/me/stats");
  },

  // Get session details
  getSessionDetails: (sessionId: string) => {
    return api.get<{ success: boolean; data: MentorshipSession }>(`/mentorship/sessions/${sessionId}`);
  },

  // Get mentor's free slots
  getMentorFreeSlots: (mentorId: string, date: string) => {
    return api.get<{ success: boolean; data: { start: string; end: string }[] }>(
      `/mentorship/mentors/${mentorId}/availability?date=${date}`
    );
  },

  // Get my availability
  getMyAvailability: () => {
    return api.get<{ success: boolean; data: MentorshipAvailability[] }>("/mentorship/availability/me");
  },

  // Add availability slot
  addAvailability: (data: { dayOfWeek: number; startTime: string; endTime: string }) => {
    return api.post<{ success: boolean; message: string; data: MentorshipAvailability }>(
      "/mentorship/availability/me",
      data
    );
  },

  // Update availability slot
  updateAvailability: (availabilityId: string, data: { dayOfWeek: number; startTime: string; endTime: string }) => {
    return api.put<{ success: boolean; message: string; data: MentorshipAvailability }>(
      `/mentorship/availability/me/${availabilityId}`,
      data
    );
  },

  // Delete availability slot
  deleteAvailability: (availabilityId: string) => {
    return api.delete<{ success: boolean; message: string }>(
      `/mentorship/availability/me/${availabilityId}`
    );
  },

  // Get unread messages count
  getUnreadCount: () => {
    return api.get<{ success: boolean; data: { totalUnread: number } }>("/mentorship/messages/unread-count");
  },

  // Get conversations
  getConversations: () => {
    return api.get<{ success: boolean; data: { user: any; lastMessage: string; lastMessageAt: string; unreadCount: number }[] }>(
      "/mentorship/messages/conversations"
    );
  },

  // Get messages with a specific user
  getMessagesWithUser: (userId: string, options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    return api.get<{ success: boolean; data: MentorshipMessage[]; pagination: any }>(
      `/mentorship/messages/${userId}${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Send a message
   sendMessage: (recipientId: string, content: string) => {
     return api.post<{ success: boolean; message: string; data: MentorshipMessage }>(
       "/mentorship/messages/send",
       { receiverId: recipientId, content }
     );
   },

  // Mark message as read
  markMessageAsRead: (messageId: string) => {
    return api.put<{ success: boolean }>(`/mentorship/messages/${messageId}/read`);
  },

  // Get reviews for a mentor
  getMentorReviews: (mentorId: string, options?: { page?: number; limit?: number; sort?: "latest" | "highest" | "lowest" }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.sort) params.append("sort", options.sort);

    return api.get<{ success: boolean; data: MentorshipReview[]; ratingDistribution: any; pagination: any }>(
      `/mentorship-reviews/mentor/${mentorId}${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Get single review by ID
  getReviewById: (reviewId: string) => {
    return api.get<{ success: boolean; data: MentorshipReview }>(`/mentorship-reviews/${reviewId}`);
  },

  // Get my reviews (as student)
  getMyReviews: (options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    return api.get<{ success: boolean; data: MentorshipReview[]; pagination: any }>(
      `/mentorship-reviews/my-reviews${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

// Create a review for a session
   createReview: (sessionId: string, rating: number, comment: string) => {
     return api.post<{ success: boolean; message: string; data: MentorshipReview }>(
       `/mentorship-reviews/session/${sessionId}`,
       { rating, comment }
     );
   },

   // Payment endpoints for mentorship sessions
   createMentorshipPayment: (sessionId: string) => {
     return api.post<{
       success: boolean;
       message: string;
       data: {
         paymentId: string;
         transactionId: string;
         paymentLink: string;
         amount: number;
         currency: string;
       };
     }>("/payments/mentorship", { sessionId });
   },

   checkMentorshipPaymentStatus: (transactionId: string) => {
     return api.get<{
       success: boolean;
       data: {
         status: string;
         transactionId: string;
         session?: string;
       };
     }>(`/payments/status/mentorship/${transactionId}`);
   },

   // Delete a review
  updateReview: (reviewId: string, rating: number, comment: string) => {
    return api.put<{ success: boolean; message: string; data: MentorshipReview }>(
      `/mentorship-reviews/${reviewId}`,
      { rating, comment }
    );
  },

  // Delete a review
  deleteReview: (reviewId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/mentorship-reviews/${reviewId}`);
  },

  // Delete a session (mentor or admin only)
  deleteSession: (sessionId: string) => {
    return api.delete<{ success: boolean; message: string; data: { sessionId: string } }>(
      `/mentorship/sessions/${sessionId}`
    );
  },

  // Get all sessions (admin only)
  getAllSessions: (options?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    mentorId?: string;
    studentId?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.status) params.append("status", options.status);
    if (options?.search) params.append("search", options.search);
    if (options?.mentorId) params.append("mentorId", options.mentorId);
    if (options?.studentId) params.append("studentId", options.studentId);
    if (options?.startDate) params.append("startDate", options.startDate);
    if (options?.endDate) params.append("endDate", options.endDate);
    if (options?.sortBy) params.append("sortBy", options.sortBy);

    return api.get<{
      success: boolean;
      data: MentorshipSession[];
      pagination: any;
    }>(
      `/mentorship/admin/sessions${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },
};